import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ShoppingBag, RefreshCw, X, Truck, CreditCard, Package, CheckCircle,
  Clock, MapPin, User, Phone, Mail, Plus, Send, Printer, Download,
  Upload, Copy, ChevronDown, MoreHorizontal, Settings, RotateCcw,
  Globe, MessageSquare, Receipt, Search, ArrowUpDown, Building2,
  FileText, Tag, Check, AlertCircle, XCircle
} from "lucide-react";
import OrderDetailView from "./OrderDetailView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(n) {
  if (!n && n !== 0) return "0";
  return Number(n).toLocaleString("en-IN");
}
function fmtDate(iso, short = false) {
  if (!iso) return "—";
  try {
    if (short) return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

// ── Status Config ──────────────────────────────────────────────────────────
const ORDER_STATUS = {
  placed:             { label: "Placed",              color: "#0369a1", bg: "#dbeafe", dot: "#3b82f6" },
  accepted:           { label: "Processing",          color: "#6d28d9", bg: "#ede9fe", dot: "#8b5cf6" },
  packed:             { label: "Packed",              color: "#0e7490", bg: "#cffafe", dot: "#06b6d4" },
  partially_shipped:  { label: "Partially Shipped",   color: "#b45309", bg: "#fef3c7", dot: "#f59e0b" },
  shipped:            { label: "Shipped",             color: "#1d4ed8", bg: "#dbeafe", dot: "#3b82f6" },
  out_for_delivery:   { label: "Out for Delivery",    color: "#c2410c", bg: "#ffedd5", dot: "#f97316" },
  delivered:          { label: "Delivered",           color: "#15803d", bg: "#dcfce7", dot: "#22c55e" },
  rejected:           { label: "Rejected",            color: "#b91c1c", bg: "#fee2e2", dot: "#ef4444" },
  cancelled:          { label: "Cancelled",           color: "#475569", bg: "#f1f5f9", dot: "#94a3b8" },
};

const ITEM_STATUS_CONFIG = {
  placed:   { label: "Placed",    color: "#0369a1", bg: "#dbeafe" },
  accepted: { label: "Accepted",  color: "#6d28d9", bg: "#ede9fe" },
  packed:   { label: "Packed",    color: "#0e7490", bg: "#cffafe" },
  shipped:  { label: "Shipped",   color: "#1d4ed8", bg: "#dbeafe" },
  cancelled:{ label: "Cancelled", color: "#475569", bg: "#f1f5f9" },
};
const PAYMENT_STATUS = {
  pending:          { label: "Pending",           color: "#92400e", bg: "#fef3c7" },
  paid:             { label: "Prepaid",           color: "#166534", bg: "#dcfce7" },
  failed:           { label: "Failed",            color: "#991b1b", bg: "#fee2e2" },
  refund_initiated: { label: "Refund Init.",      color: "#1e40af", bg: "#dbeafe" },
  refunded:         { label: "Refunded",          color: "#475569", bg: "#f1f5f9" },
};
const COD_PAYMENT = { label: "COD", color: "#475569", bg: "#f1f5f9" };

const STATUS_TRANSITIONS = {
  placed:           [{ key: "accepted", label: "Accept Order", color: "#16a34a" }, { key: "rejected", label: "Reject", color: "#dc2626" }, { key: "cancelled", label: "Cancel", color: "#64748b" }],
  accepted:         [{ key: "packed", label: "Mark as Packed", color: "#0891b2" }, { key: "cancelled", label: "Cancel", color: "#64748b" }],
  packed:           [{ key: "cancelled", label: "Cancel", color: "#64748b" }],
  shipped:          [{ key: "out_for_delivery", label: "Out for Delivery", color: "#f97316" }, { key: "cancelled", label: "Cancel", color: "#64748b" }],
  out_for_delivery: [{ key: "delivered", label: "Mark Delivered", color: "#16a34a" }],
};
const PAYMENT_TRANSITIONS = {
  pending:          [{ key: "paid", label: "Mark Paid", color: "#16a34a" }, { key: "failed", label: "Mark Failed", color: "#dc2626" }],
  paid:             [{ key: "refund_initiated", label: "Initiate Refund", color: "#0369a1" }],
  failed:           [{ key: "pending", label: "Reset Pending", color: "#64748b" }],
  refund_initiated: [{ key: "refunded", label: "Mark Refunded", color: "#16a34a" }],
};

const TIMELINE_STEPS = [
  { key: "placed",   label: "Order Placed" },
  { key: "accepted", label: "Confirmed" },
  { key: "packed",   label: "Packed" },
  { key: "shipped",  label: "Shipped" },
  { key: "out_for_delivery", label: "Out for Delivery" },
  { key: "delivered", label: "Delivered" },
];
const STEP_ORDER = TIMELINE_STEPS.map(s => s.key);

function StatusBadge({ value, map = ORDER_STATUS }) {
  const cfg = map[value] || { label: value, color: "#64748b", bg: "#f1f5f9" };
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full"
      style={{ background: cfg.bg, color: cfg.color }}>
      <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: cfg.color }} />
      {cfg.label}
    </span>
  );
}

function PaymentBadge({ method, status }) {
  const isCOD = !status || status === "pending" || (method || "").toLowerCase() === "cod";
  const cfg = isCOD ? COD_PAYMENT : (PAYMENT_STATUS[status] || COD_PAYMENT);
  return (
    <span className="inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full"
      style={{ background: cfg.bg, color: cfg.color }}>
      {isCOD ? "COD" : cfg.label}
    </span>
  );
}

// ── Stats Bar ──────────────────────────────────────────────────────────────
function StatsBar({ stats, activeFilter, onFilter }) {
  const CARDS = [
    { key: "",              label: "All Orders",  value: stats?.total,      Icon: ShoppingBag,  color: "#0369a1", bg: "#dbeafe" },
    { key: "placed",        label: "Pending",     value: stats?.pending,    Icon: Clock,        color: "#d97706", bg: "#fef3c7" },
    { key: "accepted,packed", label: "Processing",value: stats?.processing, Icon: Settings,     color: "#0e7490", bg: "#cffafe" },
    { key: "shipped,out_for_delivery", label: "Shipped", value: stats?.shipped, Icon: Truck,   color: "#1d4ed8", bg: "#dbeafe" },
    { key: "delivered",     label: "Delivered",   value: stats?.delivered,  Icon: CheckCircle,  color: "#15803d", bg: "#dcfce7" },
    { key: "cancelled",     label: "Cancelled",   value: stats?.cancelled,  Icon: XCircle,      color: "#b91c1c", bg: "#fee2e2" },
    { key: "return",        label: "Return/RTO",  value: stats?.return_rto || 0, Icon: RotateCcw, color: "#0369a1", bg: "#dbeafe" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
      {CARDS.map(c => {
        const Icon = c.Icon;
        const active = activeFilter === c.key;
        return (
          <button key={c.key} onClick={() => onFilter(c.key)}
            className={`text-left rounded-xl p-3.5 border transition-all ${active ? "border-orange-400 shadow-sm ring-1 ring-orange-200" : "border-slate-200 bg-white hover:border-slate-300"}`}
            style={{ background: active ? c.bg : undefined }}>
            <div className="flex items-center justify-between mb-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: c.bg }}>
                <Icon className="w-3.5 h-3.5" style={{ color: c.color }} />
              </div>
            </div>
            <p className="text-xl font-black text-slate-900">{c.value ?? "—"}</p>
            <p className="text-[11px] text-slate-500 font-medium mt-0.5">{c.label}</p>
          </button>
        );
      })}
    </div>
  );
}

// ── Tracking Form ──────────────────────────────────────────────────────────
function TrackingForm({ orderId, token, onDone }) {
  const [form, setForm] = useState({ courier_name: "", awb: "", tracking_url: "", estimated_delivery: "" });
  const [saving, setSaving] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.courier_name || !form.awb) { toast.error("Courier name and AWB are required"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/orders/${orderId}/shipping`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Shipping info saved — order marked Shipped!");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={handleSubmit} className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3 mt-2">
      <p className="text-xs font-bold text-blue-900 flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Shipping Details</p>
      <div className="grid grid-cols-2 gap-2">
        {[{ k: "courier_name", l: "Courier *", p: "BlueDart, DTDC…" }, { k: "awb", l: "AWB No. *", p: "BD12345678" }, { k: "tracking_url", l: "Tracking URL", p: "https://…" }, { k: "estimated_delivery", l: "Est. Delivery", p: "20 Mar 2026" }].map(f => (
          <div key={f.k}>
            <label className="text-[11px] font-semibold text-blue-800 mb-1 block">{f.l}</label>
            <input value={form[f.k]} onChange={e => setForm(p => ({ ...p, [f.k]: e.target.value }))} placeholder={f.p}
              className="w-full text-xs border border-blue-200 rounded-lg px-2.5 py-2 focus:outline-none focus:border-blue-500 bg-white" />
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg disabled:opacity-50">
          {saving ? <div className="w-3 h-3 rounded-full border border-white/40 border-t-white animate-spin" /> : <Send className="w-3 h-3" />} Confirm Shipment
        </button>
        <button type="button" onClick={onDone} className="px-3 py-1.5 text-xs text-blue-600 font-medium">Cancel</button>
      </div>
    </form>
  );
}

// ── Per-item status + tracking editor ─────────────────────────────────────
function ItemStatusBadge({ status }) {
  const cfg = ITEM_STATUS_CONFIG[status] || ITEM_STATUS_CONFIG.placed;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
      style={{ background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

function ItemShippingEditor({ item, itemIndex, orderId, token, onSaved }) {
  const savedStatus = item.item_status || "";
  const [status, setStatus] = useState(savedStatus);
  const [courier, setCourier] = useState(item.item_courier || "");
  const [trackingNo, setTrackingNo] = useState(item.item_tracking_number || "");
  const [trackingUrl, setTrackingUrl] = useState(item.item_tracking_url || "");
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const ITEM_STATUSES = [
    { value: "placed",    label: "Placed (default)" },
    { value: "accepted",  label: "Accepted" },
    { value: "packed",    label: "Packed" },
    { value: "shipped",   label: "Shipped" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const handleSave = async () => {
    if (!status) { toast.error("Select a status"); return; }
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/admin/orders/${orderId}/items/${itemIndex}`,
        { item_status: status, item_courier: courier, item_tracking_number: trackingNo, item_tracking_url: trackingUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Item updated: ${ITEM_STATUS_CONFIG[status]?.label || status}`);
      onSaved(res.data);
      setExpanded(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
      {/* Item row */}
      <div className="flex items-center gap-3 p-2.5">
        <div className="w-10 h-10 bg-white rounded-lg overflow-hidden border border-slate-200 flex-shrink-0">
          {item.image || item.product_image ? (
            <img src={item.image || item.product_image} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><Package className="w-4 h-4 text-slate-300" /></div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-slate-800 truncate">{item.product_name || item.name}</p>
          {item.variant && <p className="text-[10px] text-slate-400">{item.variant}</p>}
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-[10px] text-slate-400">Qty: {item.quantity}</span>
            {savedStatus && <ItemStatusBadge status={savedStatus} />}
            {savedStatus === "shipped" && item.item_tracking_number && (
              <span className="text-[10px] text-blue-600 font-mono">AWB: {item.item_tracking_number}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <p className="text-xs font-bold text-slate-900">₹{fmt(item.amount || item.price)}</p>
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="flex items-center gap-1 text-[11px] font-semibold text-orange-600 hover:text-orange-700 border border-orange-200 rounded-lg px-2 py-1 bg-orange-50 hover:bg-orange-100 transition-colors"
            data-testid={`item-edit-btn-${itemIndex}`}
          >
            <Truck className="w-3 h-3" />
            {expanded ? "Close" : "Update"}
          </button>
        </div>
      </div>

      {/* Expandable editor */}
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-200 bg-white space-y-2.5">
          <div>
            <label className="text-[11px] font-bold text-slate-600 mb-1 block uppercase tracking-wide">Item Status *</label>
            <div className="flex flex-wrap gap-1.5">
              {ITEM_STATUSES.map(s => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setStatus(s.value)}
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-all"
                  style={status === s.value
                    ? { background: ITEM_STATUS_CONFIG[s.value]?.bg, color: ITEM_STATUS_CONFIG[s.value]?.color, borderColor: ITEM_STATUS_CONFIG[s.value]?.color }
                    : { background: "#f8fafc", color: "#64748b", borderColor: "#e2e8f0" }
                  }
                  data-testid={`item-status-${s.value}-${itemIndex}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {status === "shipped" && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] font-semibold text-slate-500 mb-1 block">Courier Name</label>
                <input
                  value={courier} onChange={e => setCourier(e.target.value)}
                  placeholder="BlueDart, Delhivery…"
                  className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-orange-400"
                  data-testid={`item-courier-${itemIndex}`}
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 mb-1 block">Tracking Number (AWB)</label>
                <input
                  value={trackingNo} onChange={e => setTrackingNo(e.target.value)}
                  placeholder="BD12345678"
                  className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-orange-400"
                  data-testid={`item-tracking-number-${itemIndex}`}
                />
              </div>
              <div className="col-span-2">
                <label className="text-[11px] font-semibold text-slate-500 mb-1 block">Tracking URL (optional)</label>
                <input
                  value={trackingUrl} onChange={e => setTrackingUrl(e.target.value)}
                  placeholder="https://track.bluedart.com/…"
                  className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-orange-400"
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !status}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold rounded-lg disabled:opacity-50 transition-colors"
              data-testid={`item-save-btn-${itemIndex}`}
            >
              {saving ? <div className="w-3 h-3 rounded-full border border-white/40 border-t-white animate-spin" /> : <Check className="w-3 h-3" />}
              Save
            </button>
            <button type="button" onClick={() => setExpanded(false)} className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Order Detail Panel ─────────────────────────────────────────────────────
function OrderDetailPanel({ order: initialOrder, token, onClose, onRefresh, onPrint }) {
  const [order, setOrder] = useState(initialOrder);
  const [updating, setUpdating] = useState(null);
  const [showTracking, setShowTracking] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  // Keep local order state in sync when parent refreshes
  useEffect(() => { setOrder(initialOrder); }, [initialOrder]);

  const sCfg = ORDER_STATUS[order.order_status] || ORDER_STATUS.placed;
  const pCfg = PAYMENT_STATUS[order.payment_status] || PAYMENT_STATUS.pending;
  const orderActions = STATUS_TRANSITIONS[order.order_status] || [];
  const paymentActions = PAYMENT_TRANSITIONS[order.payment_status] || [];
  const total = order.total || order.subtotal || 0;
  const subtotal = order.subtotal || total;
  const isCOD = !order.payment_status || order.payment_status === "pending" || (order.payment_method || "").toLowerCase() === "cod";

  const currentStepIdx = STEP_ORDER.indexOf(order.order_status);

  const handleStatus = async (newStatus) => {
    if (newStatus === "rejected" && !rejectReason) { setShowRejectForm(true); return; }
    setUpdating(newStatus);
    try {
      await axios.put(`${API}/admin/orders/${order.id}/status`, { status: newStatus, reason: rejectReason || "" }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Status updated to ${ORDER_STATUS[newStatus]?.label || newStatus}`);
      onRefresh();
      setShowRejectForm(false); setRejectReason("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setUpdating(null); }
  };
  const handlePayment = async (newPay) => {
    setUpdating(`pay_${newPay}`);
    try {
      await axios.put(`${API}/admin/orders/${order.id}/payment`, { payment_status: newPay }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Payment status updated");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setUpdating(null); }
  };
  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/admin/orders/${order.id}/note`, { note: noteText }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Note added");
      setNoteText(""); setAddingNote(false);
      onRefresh();
    } catch (e) { toast.error("Failed to save note"); }
    finally { setSavingNote(false); }
  };

  return (
    <div className="h-full flex flex-col bg-white border-l border-slate-200">
      {/* Panel Header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 flex-shrink-0">
        <h2 className="text-sm font-bold text-slate-900">Order Details</h2>
        <div className="flex items-center gap-2">
          <button onClick={onPrint} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-800 border border-slate-200 rounded-lg px-2.5 py-1.5 transition-colors">
            <Printer className="w-3.5 h-3.5" /> Print Invoice
          </button>
          <button onClick={onClose} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Order ID + Status */}
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-1.5">
                <span className="text-base font-black text-slate-900">#{order.order_number}</span>
                <button onClick={() => { navigator.clipboard.writeText(order.order_number); toast.success("Copied!"); }}
                  className="text-slate-400 hover:text-slate-600 transition-colors">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {fmtDate(order.created_at)} · <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" /> Website</span>
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <StatusBadge value={order.order_status} />
              <PaymentBadge method={order.payment_method} status={order.payment_status} />
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <section className="px-5 py-4 border-b border-slate-100">
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">Customer Information</p>
          <div className="space-y-1.5">
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-black text-orange-700">{(order.customer_name || "C")[0].toUpperCase()}</span>
              </div>
              {order.customer_name}
            </p>
            {order.customer_phone && (
              <p className="flex items-center gap-2 text-xs text-slate-600">
                <Phone className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /> {order.customer_phone}
              </p>
            )}
            {order.customer_email && !order.customer_email.includes(".local") && (
              <p className="flex items-center gap-2 text-xs text-slate-600">
                <Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /> {order.customer_email}
              </p>
            )}
            {order.delivery_address && (
              <p className="flex items-start gap-2 text-xs text-slate-600">
                <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" /> {order.delivery_address}
              </p>
            )}
          </div>
        </section>

        {(order.shipments || []).length > 1 && (
          <section className="px-5 py-4 border-b border-slate-100" data-testid="order-shipments">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">Shipments ({order.shipments.length})</p>
            <div className="space-y-2">
              {order.shipments.map((sh, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-2.5 text-xs">
                  <div className="flex justify-between font-semibold text-slate-800">
                    <span>{sh.name} · {sh.dispatch_text}</span>
                    <span>{Number(sh.delivery_charge) > 0 ? `₹${fmt(sh.delivery_charge)}` : ""}</span>
                  </div>
                  <p className="text-slate-500 mt-0.5">{(sh.items || []).map(it => `${it.name}${it.variant ? ` (${it.variant})` : ""} × ${it.quantity}`).join(", ")}</p>
                  <p className="text-slate-400">Weight: {Number(sh.weight_g) >= 1000 ? `${(sh.weight_g / 1000).toFixed(2)}kg` : `${sh.weight_g}g`}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Items — each with per-item status + tracking */}
        <section className="px-5 py-4 border-b border-slate-100" data-testid="order-items-section">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Items ({(order.items || []).length})
            </p>
            <span className="text-[10px] text-slate-400">Click "Update" on any item to set its status &amp; courier</span>
          </div>
          <div className="space-y-2">
            {(order.items || []).map((item, i) => (
              <ItemShippingEditor
                key={i}
                item={item}
                itemIndex={i}
                orderId={order.id}
                token={token}
                onSaved={(data) => {
                  // Update local items + order status immediately
                  setOrder(prev => ({
                    ...prev,
                    order_status: data.order_status,
                    items: data.items || prev.items,
                  }));
                  onRefresh();
                }}
              />
            ))}
          </div>
        </section>

        {/* Order Summary */}
        <section className="px-5 py-4 border-b border-slate-100">
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">Order Summary</p>
          <div className="space-y-2">
            {[
              { label: "Subtotal", value: `₹${fmt(subtotal)}` },
              ...(order.delivery_charge ? [{ label: "Shipping Charges", value: `₹${fmt(order.delivery_charge)}` }] : []),
              ...(order.payment_surcharge ? [{ label: "Payment Surcharge", value: `₹${fmt(order.payment_surcharge)}` }] : []),
              ...(order.coupon_discount ? [{ label: `Discount ${order.coupon_code ? `(${order.coupon_code})` : ""}`, value: `-₹${fmt(order.coupon_discount)}`, green: true }] : []),
            ].map((r, i) => (
              <div key={i} className="flex justify-between items-center">
                <span className="text-xs text-slate-500">{r.label}</span>
                <span className={`text-xs font-semibold ${r.green ? "text-green-600" : "text-slate-700"}`}>{r.value}</span>
              </div>
            ))}
            <div className="flex justify-between items-center pt-2 border-t border-slate-200">
              <span className="text-sm font-bold text-slate-900">Total Amount</span>
              <span className="text-base font-black text-slate-900">₹{fmt(total)}</span>
            </div>
          </div>
        </section>

        {/* Payment Information */}
        <section className="px-5 py-4 border-b border-slate-100">
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">Payment Information</p>
          <div className="space-y-2">
            {[
              { label: "Method", value: order.payment_method || "COD" },
              ...(order.razorpay_payment_id ? [{ label: "Transaction ID", value: order.razorpay_payment_id, mono: true }] : []),
              { label: "Payment Date", value: order.payment_status === "paid" ? fmtDate(order.updated_at, true) : "Pending" },
            ].map((r, i) => (
              <div key={i} className="flex justify-between items-center">
                <span className="text-xs text-slate-500">{r.label}</span>
                <span className={`text-xs font-semibold text-slate-700 ${r.mono ? "font-mono" : ""}`}>{r.value}</span>
              </div>
            ))}
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-500">Payment Status</span>
              <PaymentBadge method={order.payment_method} status={order.payment_status} />
            </div>
          </div>
          {/* Payment Actions */}
          {paymentActions.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {paymentActions.map(a => (
                <button key={a.key} onClick={() => handlePayment(a.key)} disabled={updating === `pay_${a.key}`}
                  data-testid={`payment-action-${a.key}-${order.id}`}
                  className="flex items-center gap-1 px-3 py-1.5 text-white text-xs font-bold rounded-lg disabled:opacity-50 transition-colors"
                  style={{ background: a.color }}>
                  {updating === `pay_${a.key}` ? <div className="w-3 h-3 rounded-full border border-white/40 border-t-white animate-spin" /> : null}
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* GST Details (only if present) */}
        {order.gst_details && (order.gst_details.gstin || order.gst_details.business_name) && (
          <section className="px-5 py-4 border-b border-slate-100">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Receipt className="w-3.5 h-3.5" /> GST / Tax Invoice
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 space-y-2">
              {order.gst_details.business_name && (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-amber-700 font-medium flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" /> Business Name
                  </span>
                  <span className="text-xs font-bold text-amber-900">{order.gst_details.business_name}</span>
                </div>
              )}
              {order.gst_details.gstin && (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-amber-700 font-medium flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5" /> GSTIN
                  </span>
                  <span className="text-xs font-bold text-amber-900 font-mono tracking-wider">{order.gst_details.gstin}</span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Tracking Info */}
        {order.tracking?.awb && (
          <section className="px-5 py-4 border-b border-slate-100">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5" /> Shipment Tracking
            </p>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3.5 space-y-1.5">
              <p className="text-xs text-blue-800"><strong>Courier:</strong> {order.tracking.courier_name}</p>
              <p className="text-xs text-blue-800"><strong>AWB:</strong> {order.tracking.awb}</p>
              {order.tracking.estimated_delivery && <p className="text-xs text-blue-800"><strong>ETA:</strong> {order.tracking.estimated_delivery}</p>}
              {order.tracking.tracking_url && (
                <a href={order.tracking.tracking_url} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-600 font-semibold hover:underline block mt-1">Track Shipment →</a>
              )}
            </div>
          </section>
        )}

        {/* Order Timeline */}
        <section className="px-5 py-4 border-b border-slate-100">
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-4">Order Timeline</p>
          <div className="space-y-0">
            {TIMELINE_STEPS.map((step, idx) => {
              const done = currentStepIdx >= idx;
              const current = currentStepIdx === idx;
              const last = idx === TIMELINE_STEPS.length - 1;
              return (
                <div key={step.key} className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${done ? "bg-green-500" : "bg-slate-100 border border-slate-300"}`}>
                      {done ? <Check className="w-3 h-3 text-white" /> : <div className="w-2 h-2 rounded-full bg-slate-300" />}
                    </div>
                    {!last && <div className={`w-0.5 h-6 mt-0.5 ${done ? "bg-green-300" : "bg-slate-200"}`} />}
                  </div>
                  <div className="pb-3">
                    <p className={`text-xs font-semibold ${done ? "text-slate-800" : "text-slate-400"}`}>{step.label}</p>
                    {current && <p className="text-[10px] text-slate-400 mt-0.5">{fmtDate(order.updated_at)}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Order Status Actions */}
        {(orderActions.length > 0 || order.order_status === "packed") && (
          <section className="px-5 py-4 border-b border-slate-100">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">Update Status</p>
            <div className="flex flex-wrap gap-2">
              {orderActions.map(a => (
                <button key={a.key} onClick={() => handleStatus(a.key)} disabled={!!updating}
                  data-testid={`order-action-${a.key}-${order.id}`}
                  className="flex items-center gap-1 px-3 py-1.5 text-white text-xs font-bold rounded-lg disabled:opacity-50 transition-colors"
                  style={{ background: a.color }}>
                  {updating === a.key ? <div className="w-3 h-3 rounded-full border border-white/40 border-t-white animate-spin" /> : null}
                  {a.label}
                </button>
              ))}
            </div>
            {showRejectForm && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-2 mt-2">
                <input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Rejection reason…"
                  className="w-full text-xs border border-red-300 rounded-lg px-2.5 py-2 focus:outline-none" />
                <div className="flex gap-2">
                  <button onClick={() => handleStatus("rejected")} className="px-3 py-1.5 bg-red-600 text-white text-xs font-bold rounded-lg">Confirm</button>
                  <button onClick={() => setShowRejectForm(false)} className="px-3 py-1.5 text-red-600 text-xs font-medium">Cancel</button>
                </div>
              </div>
            )}
            {order.order_status === "packed" && !showTracking && (
              <button onClick={() => setShowTracking(true)}
                className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors">
                <Truck className="w-3.5 h-3.5" /> Add Tracking & Ship
              </button>
            )}
            {showTracking && (
              <TrackingForm orderId={order.id} token={token} onDone={() => { setShowTracking(false); onRefresh(); }} />
            )}
          </section>
        )}

        {/* Emails sent */}
        {(order.emails_sent || []).length > 0 && (
          <section className="px-5 py-4 border-b border-slate-100">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Emails Sent</p>
            <div className="flex flex-wrap gap-1">
              {order.emails_sent.map(e => (
                <span key={e} className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">{e.replace(/_/g, " ")}</span>
              ))}
            </div>
          </section>
        )}

        {/* Admin Notes */}
        <section className="px-5 py-4 pb-8">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" /> Order Notes
            </p>
            {!addingNote && (
              <button onClick={() => setAddingNote(true)}
                className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-800 transition-colors">
                <Plus className="w-3 h-3" /> Add Note
              </button>
            )}
          </div>
          {/* Existing customer note */}
          {order.notes && (
            <div className="bg-slate-50 rounded-xl p-3 mb-2">
              <p className="text-[10px] text-slate-400 font-semibold mb-0.5">Customer Note</p>
              <p className="text-xs text-slate-600">{order.notes}</p>
            </div>
          )}
          {/* Admin notes */}
          {(order.admin_notes || []).map((note, i) => (
            <div key={i} className="bg-amber-50 border border-amber-100 rounded-xl p-3 mb-2">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 rounded-full bg-slate-700 flex items-center justify-center">
                    <span className="text-[9px] font-bold text-white">{(note.author || "A")[0].toUpperCase()}</span>
                  </div>
                  <span className="text-[11px] font-semibold text-slate-700">{note.author || "Admin"}</span>
                </div>
                <span className="text-[10px] text-slate-400">{fmtDate(note.created_at, true)}</span>
              </div>
              <p className="text-xs text-slate-600">{note.text}</p>
            </div>
          ))}
          {/* Add note form */}
          {addingNote && (
            <div className="space-y-2 mt-2">
              <textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={3}
                placeholder="Add an internal note…"
                className="w-full text-xs border border-slate-300 rounded-xl px-3 py-2 focus:outline-none focus:border-orange-500 resize-none" />
              <div className="flex gap-2">
                <button onClick={handleAddNote} disabled={savingNote || !noteText.trim()}
                  className="flex items-center gap-1 px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold rounded-lg disabled:opacity-50">
                  {savingNote ? <div className="w-3 h-3 rounded-full border border-white/40 border-t-white animate-spin" /> : <Send className="w-3 h-3" />} Save Note
                </button>
                <button onClick={() => { setAddingNote(false); setNoteText(""); }} className="px-3 py-1.5 text-xs text-slate-500 font-medium">Cancel</button>
              </div>
            </div>
          )}
          {!(order.notes) && !(order.admin_notes?.length) && !addingNote && (
            <p className="text-xs text-slate-400 italic">No notes yet.</p>
          )}
        </section>
      </div>
    </div>
  );
}

// ── Create Order Modal ─────────────────────────────────────────────────────
function CreateOrderModal({ token, onClose, onCreated }) {
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    delivery_address: "", payment_method: "COD", notes: "",
    items: [{ product_name: "", quantity: 1, price: "", amount: "", variant: "", image: "" }],
  });
  const [saving, setSaving] = useState(false);
  const updateItem = (idx, patch) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], ...patch };
    if (patch.price !== undefined || patch.quantity !== undefined) {
      items[idx].amount = String(parseFloat(items[idx].price || 0) * parseFloat(items[idx].quantity || 1));
    }
    setForm(p => ({ ...p, items }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || !form.delivery_address) { toast.error("Name, email and address are required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/orders`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Order created!"); onCreated(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create order"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-sm font-bold text-slate-900">Create New Order</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-auto p-6 space-y-5">
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Customer Details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[{ key: "customer_name", label: "Full Name *", type: "text", placeholder: "Priya Sharma" }, { key: "customer_email", label: "Email *", type: "email", placeholder: "priya@example.com" }, { key: "customer_phone", label: "Phone", type: "tel", placeholder: "+91 98765 43210" }].map(f => (
                <div key={f.key}>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">{f.label}</label>
                  <input type={f.type} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder}
                    className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500" />
                </div>
              ))}
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Payment Method</label>
                <select value={form.payment_method} onChange={e => setForm(p => ({ ...p, payment_method: e.target.value }))}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 bg-white">
                  {["COD", "UPI", "Card", "Net Banking", "Wallet"].map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Delivery Address *</label>
              <textarea value={form.delivery_address} onChange={e => setForm(p => ({ ...p, delivery_address: e.target.value }))} rows={2} placeholder="42, MG Road, Bengaluru…"
                className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 resize-none" />
            </div>
          </div>
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Order Items</p>
            <div className="space-y-3">
              {form.items.map((item, idx) => (
                <div key={idx} className="border border-slate-200 rounded-xl p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2">
                      <label className="text-[11px] font-semibold text-slate-500 mb-1 block">Product Name *</label>
                      <input value={item.product_name} onChange={e => updateItem(idx, { product_name: e.target.value })} placeholder="Product name"
                        className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500" />
                    </div>
                    {[{ k: "price", l: "Price (₹)", t: "number", p: "499" }, { k: "quantity", l: "Quantity", t: "number", p: "1" }, { k: "variant", l: "Variant", t: "text", p: "Color: Blue" }].map(f => (
                      <div key={f.k}>
                        <label className="text-[11px] font-semibold text-slate-500 mb-1 block">{f.l}</label>
                        <input type={f.t} value={item[f.k]} min={f.k === "quantity" ? 1 : undefined} onChange={e => updateItem(idx, { [f.k]: e.target.value })} placeholder={f.p}
                          className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500" />
                      </div>
                    ))}
                    <div>
                      <label className="text-[11px] font-semibold text-slate-500 mb-1 block">Amount (₹)</label>
                      <input value={item.amount} readOnly className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 text-slate-500" />
                    </div>
                  </div>
                  {form.items.length > 1 && (
                    <button type="button" onClick={() => setForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }))}
                      className="text-xs text-red-500 hover:text-red-700 font-medium flex items-center gap-1">
                      <X className="w-3 h-3" /> Remove
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => setForm(p => ({ ...p, items: [...p.items, { product_name: "", quantity: 1, price: "", amount: "", variant: "" }] }))}
                className="w-full py-2 border-2 border-dashed border-slate-300 hover:border-orange-400 rounded-xl text-xs font-semibold text-slate-500 hover:text-orange-600 transition-colors flex items-center justify-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Add Item
              </button>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} data-testid="create-order-submit"
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-bold rounded-xl text-sm transition-colors">
              {saving ? <div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> : <Plus className="w-4 h-4" />}
              {saving ? "Creating…" : "Create Order & Send Email"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
// Date filter (same presets as the Dashboard)
const DATE_PRESETS = [
  { key: "all",       label: "All Dates" },
  { key: "today",     label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d",        label: "Last 7 Days" },
  { key: "30d",       label: "Last 30 Days" },
  { key: "month",     label: "This Month" },
  { key: "custom",    label: "Custom Range" },
];

function localDay(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// Returns [startMs, endMs) in the browser's local time, or null for "All Dates".
export function dateWindow(preset, from, to, now = new Date()) {
  const startOf = (y, m, d) => new Date(y, m, d).getTime();
  const today = startOf(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = startOf(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const daysAgo = (n) => startOf(now.getFullYear(), now.getMonth(), now.getDate() - n);
  switch (preset) {
    case "today":     return [today, tomorrow];
    case "yesterday": return [daysAgo(1), today];
    case "7d":        return [daysAgo(6), tomorrow];
    case "30d":       return [daysAgo(29), tomorrow];
    case "month":     return [startOf(now.getFullYear(), now.getMonth(), 1), tomorrow];
    case "custom": {
      if (!from || !to) return null;
      const [fy, fm, fd] = from.split("-").map(Number);
      const [ty, tm, td] = to.split("-").map(Number);
      const a = startOf(fy, fm - 1, fd), b = startOf(ty, tm - 1, td + 1);
      return a <= b ? [a, b] : [startOf(ty, tm - 1, td), startOf(fy, fm - 1, fd + 1)];
    }
    default: return null;
  }
}

export default function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [payFilter, setPayFilter] = useState("");
  const [datePreset, setDatePreset] = useState("all");
  const [dateFrom, setDateFrom] = useState(() => localDay(new Date()));
  const [dateTo, setDateTo] = useState(() => localDay(new Date()));
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const PER_PAGE = 12;
  const token = localStorage.getItem("it_token");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Load every order once; status and date filters run here so the cards can follow the date range
      const [ordersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/orders?limit=1000`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/orders/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
      // Refresh selected order if open
      if (selectedOrder) {
        const updated = ordersRes.data.find(o => o.id === selectedOrder.id);
        if (updated) setSelectedOrder(updated);
      }
    } catch { toast.error("Failed to load orders"); }
    finally { setLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Open the order passed in the link, e.g. from the dashboard's Recent Orders
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get("order");
    const found = id && orders.find(o => o.id === id);
    if (found) setSelectedOrder(found);
  }, [orders, searchParams]);

  // Reset page when filter changes
  useEffect(() => { setPage(1); }, [statusFilter, search, payFilter, datePreset, dateFrom, dateTo]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const dateFiltered = useMemo(() => {
    const win = dateWindow(datePreset, dateFrom, dateTo);
    if (!win) return orders;
    return orders.filter(o => {
      const t = Date.parse(o.created_at);
      return !Number.isNaN(t) && t >= win[0] && t < win[1];
    });
  }, [orders, datePreset, dateFrom, dateTo]);

  // Status cards: all-time numbers from the server, or counted from the orders in the chosen dates
  const shownStats = useMemo(() => {
    if (datePreset === "all") return stats;
    const count = (...st) => dateFiltered.filter(o => st.includes(o.order_status)).length;
    return {
      total: dateFiltered.length,
      pending: count("placed"),
      processing: count("accepted", "packed"),
      shipped: count("shipped", "out_for_delivery"),
      delivered: count("delivered"),
      cancelled: count("cancelled", "rejected"),
      return_rto: 0,
    };
  }, [datePreset, dateFiltered, stats]);

  const filtered = useMemo(() => {
    let list = [...dateFiltered];
    const q = search.toLowerCase();
    if (q) list = list.filter(o =>
      (o.order_number || "").toLowerCase().includes(q) ||
      (o.customer_name || "").toLowerCase().includes(q) ||
      (o.customer_phone || "").includes(q) ||
      (o.customer_email || "").toLowerCase().includes(q)
    );
    if (payFilter === "cod") list = list.filter(o => !o.payment_status || o.payment_status === "pending" || (o.payment_method || "").toLowerCase() === "cod");
    if (payFilter === "prepaid") list = list.filter(o => o.payment_status === "paid");
    if (statusFilter) {
      const statuses = statusFilter.split(",");
      list = list.filter(o => statuses.includes(o.order_status));
    }
    list.sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [dateFiltered, search, payFilter, statusFilter, sortKey, sortDir]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const SortBtn = ({ col }) => (
    <button onClick={() => handleSort(col)} className="inline-flex ml-1 text-slate-400 hover:text-slate-600">
      <ArrowUpDown className="w-3 h-3" />
    </button>
  );

  // ── CSV Export ───────────────────────────────────────────────────
  const handleExportCSV = () => {
    const rows = filtered;
    const headers = [
      "Order Number", "Date", "Customer Name", "Customer Email", "Customer Phone",
      "Order Status", "Payment Method", "Payment Status",
      "Items Count", "Subtotal (₹)", "Delivery Charge (₹)", "Coupon Code",
      "Coupon Discount (₹)", "Payment Surcharge (₹)", "Total (₹)",
      "GST Business Name", "GSTIN", "Delivery Address"
    ];
    const escape = v => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csvLines = [
      headers.map(escape).join(","),
      ...rows.map(o => {
        const gst = o.gst_details || {};
        const ic = (o.items || []).reduce((s, i) => s + Number(i.quantity || 1), 0);
        return [
          o.order_number, (o.created_at || "").slice(0, 10),
          o.customer_name, o.customer_email, o.customer_phone,
          o.order_status, o.payment_method, o.payment_status,
          ic, o.subtotal || o.total, o.delivery_charge || 0,
          o.coupon_code || "", o.coupon_discount || 0, o.payment_surcharge || 0, o.total,
          gst.business_name || "", gst.gstin || "",
          (o.delivery_address || "").replace(/\n/g, " ")
        ].map(escape).join(",");
      })
    ];
    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `orders_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} orders to CSV`);
  };

  const TABLE_COLS = [    { key: "order_number", label: "Order ID" },
    { key: "created_at",   label: "Date" },
    { key: "customer_name", label: "Customer" },
    { key: "items",         label: "Items", noSort: true },
    { key: "total",         label: "Amount (₹)" },
    { key: "payment_status", label: "Payment", noSort: true },
    { key: "order_status",   label: "Status", noSort: true },
    { key: "actions",        label: "", noSort: true },
  ];

  return (
    <>
      {/* Full-page detail view when an order is selected */}
      {selectedOrder ? (
        <div className="min-h-full overflow-y-auto" style={{ height: "calc(100vh - 64px)" }}>
          <OrderDetailView
            key={selectedOrder.id}
            order={selectedOrder}
            token={token}
            onBack={() => setSelectedOrder(null)}
            onRefresh={() => {
              load();
              // Also refresh the selected order from the latest data
              if (selectedOrder) {
                axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } })
                  .then(res => {
                    const updated = res.data.find(o => o.id === selectedOrder.id);
                    if (updated) setSelectedOrder(updated);
                  }).catch(() => {});
              }
            }}
          />
        </div>
      ) : (
        /* Orders list view */
        <div className="flex h-full min-h-0 overflow-hidden" style={{ height: "calc(100vh - 64px)" }}>
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-1">
            <div>
              <p className="text-xs text-slate-400 mb-1">Orders &rsaquo; All Orders</p>
              <h1 className="text-xl font-black text-slate-900">Orders</h1>
              <p className="text-xs text-slate-500 mt-0.5">Manage and track all your orders from the website.</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 mt-1">
              <button onClick={load} title="Refresh" className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-2 transition-colors hover:bg-slate-50">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              </button>
              <button onClick={handleExportCSV}
                className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-2 transition-colors hover:bg-slate-50">
                <Download className="w-3.5 h-3.5" /> Export ({filtered.length})
              </button>
              <button className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-2 transition-colors hover:bg-slate-50">
                <Upload className="w-3.5 h-3.5" /> Import
              </button>
              <button data-testid="create-order-btn" onClick={() => setShowCreate(true)}
                className="flex items-center gap-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-3.5 py-2 font-bold transition-colors shadow-sm">
                <Plus className="w-3.5 h-3.5" /> Create Order
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="mt-5">
            <StatsBar stats={shownStats} activeFilter={statusFilter} onFilter={key => { setStatusFilter(key); setPage(1); }} />
          </div>

          {/* Filter Bar */}
          <div className="flex flex-wrap gap-2 mb-4">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                data-testid="orders-search"
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by Order ID, Customer, Mobile…"
                className="w-full text-xs pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:border-orange-400 bg-white"
              />
            </div>
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              data-testid="orders-status-filter"
              className="text-xs border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-orange-400 bg-white text-slate-700 font-medium">
              <option value="">All Status</option>
              {Object.entries(ORDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={payFilter} onChange={e => setPayFilter(e.target.value)}
              data-testid="orders-payment-filter"
              className="text-xs border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-orange-400 bg-white text-slate-700 font-medium">
              <option value="">All Payment</option>
              <option value="prepaid">Prepaid</option>
              <option value="cod">COD</option>
            </select>
            <select value={datePreset} onChange={e => setDatePreset(e.target.value)}
              data-testid="orders-date-filter"
              className="text-xs border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:border-orange-400 bg-white text-slate-700 font-medium">
              {DATE_PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
            {datePreset === "custom" && (
              <div className="flex items-center gap-1.5 bg-white border border-orange-200 rounded-xl px-3 py-1.5" data-testid="orders-custom-range">
                <input type="date" value={dateFrom} max={dateTo} onChange={e => setDateFrom(e.target.value)}
                  data-testid="orders-date-from"
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:border-orange-400" />
                <span className="text-xs text-slate-400">to</span>
                <input type="date" value={dateTo} min={dateFrom} max={localDay(new Date())} onChange={e => setDateTo(e.target.value)}
                  data-testid="orders-date-to"
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:border-orange-400" />
              </div>
            )}
          </div>

          {/* Table */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="px-4 py-3 w-10">
                      <input type="checkbox" className="rounded border-slate-300" />
                    </th>
                    {TABLE_COLS.map(col => (
                      <th key={col.key} className="px-3 py-3 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                        {col.label}
                        {!col.noSort && <SortBtn col={col.key} />}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={9} className="text-center py-16">
                      <div className="flex justify-center"><div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
                    </td></tr>
                  ) : paged.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-16">
                      <ShoppingBag className="w-10 h-10 mx-auto mb-3 text-slate-200" />
                      <p className="text-sm font-semibold text-slate-500">No orders found</p>
                    </td></tr>
                  ) : paged.map(order => {
                    const total = order.total || order.subtotal || 0;
                    const itemCount = (order.items || []).length;
                    const firstImg = (order.items || [])[0]?.image || (order.items || [])[0]?.product_image;
                    const isSelected = selectedOrder?.id === order.id;
                    return (
                      <tr key={order.id}
                        data-testid={`order-row-${order.id}`}
                        onClick={() => setSelectedOrder(isSelected ? null : order)}
                        className={`cursor-pointer transition-colors ${isSelected ? "bg-orange-50 hover:bg-orange-100" : "hover:bg-slate-50"}`}>
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" className="rounded border-slate-300" />
                        </td>
                        <td className="px-3 py-3">
                          <span className="text-xs font-bold text-blue-600 hover:text-blue-800">{order.order_number}</span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <p className="text-xs font-medium text-slate-700">{fmtDate(order.created_at, true)}</p>
                          <p className="text-[10px] text-slate-400">{new Date(order.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</p>
                        </td>
                        <td className="px-3 py-3">
                          <p className="text-xs font-semibold text-slate-800">{order.customer_name}</p>
                          {order.customer_phone && <p className="text-[10px] text-slate-400">{order.customer_phone}</p>}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5">
                            {firstImg && (
                              <img src={firstImg} alt="" className="w-7 h-7 rounded-lg object-cover border border-slate-100 flex-shrink-0" />
                            )}
                            <span className="text-xs text-slate-600 font-medium">{itemCount} item{itemCount !== 1 ? "s" : ""}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="text-xs font-bold text-slate-900">₹{fmt(total)}</span>
                        </td>
                        <td className="px-3 py-3">
                          <PaymentBadge method={order.payment_method} status={order.payment_status} />
                        </td>
                        <td className="px-3 py-3">
                          <StatusBadge value={order.order_status} />
                        </td>
                        <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                          <button className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors">
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filtered.length > 0 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50">
                <p className="text-xs text-slate-500">
                  Showing {Math.min((page - 1) * PER_PAGE + 1, filtered.length)} to {Math.min(page * PER_PAGE, filtered.length)} of <strong>{filtered.length}</strong> orders
                </p>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="px-2.5 py-1.5 text-xs font-semibold border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-100 transition-colors">‹</button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let num = i + 1;
                    if (totalPages > 5) {
                      if (page <= 3) num = i + 1;
                      else if (page >= totalPages - 2) num = totalPages - 4 + i;
                      else num = page - 2 + i;
                    }
                    return (
                      <button key={num} onClick={() => setPage(num)}
                        className={`px-2.5 py-1.5 text-xs font-semibold border rounded-lg transition-colors ${page === num ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 hover:bg-slate-100"}`}>
                        {num}
                      </button>
                    );
                  })}
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="px-2.5 py-1.5 text-xs font-semibold border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-100 transition-colors">›</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

        {showCreate && <CreateOrderModal token={token} onClose={() => setShowCreate(false)} onCreated={load} />}
      </div>
      )}
    </>
  );
}
